import { useMemo } from 'react';
import { useLocale } from '@mypoz/i18n';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatUnreadBadge,
} from '@mypoz/shared';

/**
 * Binds the formatters in @mypoz/shared to whichever language the user picked, so a component
 * never has to thread a locale through by hand — and so nothing can quietly hardcode 'en-MY'.
 */
export function useFormat() {
  const { intlLocale } = useLocale();

  return useMemo(
    () => ({
      locale: intlLocale,
      money: (cents: number, currency?: string) => formatMoney(cents, intlLocale, currency),
      number: (value: number, fractionDigits?: number) =>
        formatNumber(value, intlLocale, fractionDigits),
      date: (iso: string) => formatDate(iso, intlLocale),
      dateTime: (iso: string) => formatDateTime(iso, intlLocale),
      /**
       * The small fixed-width unread badges only (header bell, sidebar) — capped at "9+" past 9.
       * The Notifications page's own "14 unread" sentence uses `number` instead: it's prose, not a
       * badge, so it keeps the exact count (MYP-66).
       */
      unreadBadge: (count: number) => formatUnreadBadge(count, intlLocale),
    }),
    [intlLocale],
  );
}
